import { memo, useCallback, useMemo, useRef } from "react";
import "./multi-select.scss";
import { FormInputElement } from "@archetypes/form-element";
import { LabelInfo } from "@components/label-info";
import { FormError } from "@components/form-error";
import { Chip } from "@components/chip";
import { Action } from "@components/action/action";
import { COption } from "@components/combobox";

interface MultiSelectProps<T> extends FormInputElement {
  onChange?: (value: T[]) => void;
  value?: T[];
  options?: T[];
  getKey: (value: T) => string | number;
  renderOption: (option: T) => JSX.Element;
  renderValue: (option?: T) => string | undefined;
  containerClass?: string;
  label?: string;
  errorMessage?: string;
  labelClassName?: string;
  infoText?: string;
}

const MultiSelectComponent = <T,>({
  value,
  onChange,
  options,
  placeholder = "Select items",
  renderValue,
  renderOption,
  getKey,
  containerClass,
  label,
  errorMessage,
  labelClassName,
  required,
  infoText,
  ...restProps
}: MultiSelectProps<T>) => {
  const multiSelectBox = useRef<HTMLDivElement>(null);
  const valueKeys = useMemo(() => value?.map((v) => getKey(v)), [value, getKey]);
  const onOptionClick = useCallback(
    (option: T) => {
      Array.isArray(value) ? onChange?.([...value, option]) : onChange?.([option]);
    },
    [onChange, value],
  );
  const removeItem = useCallback(
    (item: T) => {
      const itemKey = getKey(item);
      onChange?.(value?.filter((val) => getKey(val) !== itemKey) || []);
    },
    [onChange, value, getKey],
  );
  return (
    <div className={`multi-select-wrapper ${containerClass}`}>
      <LabelInfo
        label={label}
        labelClassName={labelClassName}
        required={required}
        infoText={infoText}
      />
      <>
        <Action
          mode="select"
          {...restProps}
          label={
            value?.length ? (
              <div className="multi-select-box-wrapper" ref={multiSelectBox}>
                <div className="multi-select-selected-group">
                  {value?.slice(0, 2).map((item) => {
                    return (
                      <Chip
                        key={getKey(item)}
                        variant="primary"
                        value={renderValue(item)}
                        className="pr-5"
                        onRemove={() => {
                          removeItem(item);
                        }}
                        textLimit={130}
                      />
                    );
                  })}
                  {value && value?.length > 2 && (
                    <Chip
                      value={`+${value?.length - 2}`}
                      variant="primary"
                      className="pointer-none"
                    />
                  )}
                </div>
              </div>
            ) : (
              placeholder
            )
          }
          containerClassName="mx-h-200"
          keepOpenOnClick
        >
          {options?.map((option) => {
            const optionKey = getKey(option);
            const isOptionActive = valueKeys?.includes(optionKey);
            return (
              <COption
                value={option}
                key={optionKey}
                checked={isOptionActive}
                className="w-100"
                onClick={() => {
                  if (!isOptionActive) {
                    onOptionClick(option);
                  } else {
                    removeItem(option);
                  }
                }}
              >
                {renderOption(option)}
              </COption>
            );
          })}
        </Action>
      </>
      <FormError errorMessage={errorMessage} />
    </div>
  );
};

export const MultiSelect = memo(MultiSelectComponent) as typeof MultiSelectComponent;
